// @flow

import * as React from 'react';

import { webhosting } from '@gandi/cart';
import type { DecoratedProduct } from '@gandi/cart';
import { Base, Placeholder } from '@gandi/ui';

import { useFeatureFlags } from 'src/hooks/useFeatureFlag';

import {
  getPrice,
  getPriceBeforeDiscount,
  useAvailableSizes,
  useConfigurationDetails,
} from '../modules/webhosting';

import { WebHostingPlanRows } from './WebHostingPlanRows';

import { ToggleGroupRow } from '.';

export type Props = {
  product: DecoratedProduct,
  /** pass to `isBusy` a boolean that represent its business but `product.isWorking` */
  isBusy?: boolean,
  updateSizeHandler: (evt: SyntheticInputEvent<>) => Promise<void>,
  removeLowerThanActual?: boolean,
};

export function WebHostingSizeToggleGroupRow({
  product,
  isBusy = false,
  updateSizeHandler,
  removeLowerThanActual = false,
}: Props): React.MixedElement {
  const { FF_CART_OVERVIEW_UPSELL } = useFeatureFlags();

  /// size change
  const sizes = useAvailableSizes(
    product.payload.duration === 1 ? 'monthly' : 'yearly',
    product.payload.extra?.application,
  );

  // we may need to remove the lower choices
  let filteredSizes;
  const actualSize = webhosting.getActualSize(product.payload);
  if (sizes && removeLowerThanActual && actualSize) {
    const i = sizes.findIndex(s => s.value === actualSize);
    filteredSizes = sizes.slice(i + 1);
  }

  const selectedSize = product.payload.size || 'starter';

  const configurationDetails = useConfigurationDetails({
    duration: product.payload.duration || 1,
    size:
      (typeof product.payload.size === 'string' && product.payload.size) ||
      'starter',
  });

  if (FF_CART_OVERVIEW_UPSELL) {
    const options = filteredSizes || sizes;
    return (
      <WebHostingPlanRows
        disabled={!getPrice({ product, isBusy })}
        onChange={updateSizeHandler}
        options={options}
        product={product}
      />
    );
  }

  // Legacy style
  return (
    <ToggleGroupRow
      options={filteredSizes || sizes}
      value={selectedSize}
      onChange={updateSizeHandler}
      total={getPriceBeforeDiscount({ product, isBusy })}
      detail={
        !!configurationDetails &&
        // we still want the details if product.total?.before_taxes === 0 (which could mean "error")
        (getPrice({ product, isBusy }) || product.total?.before_taxes === 0) ? (
          configurationDetails
        ) : (
          <Base flex paddingTop="xsmall">
            <Placeholder chars={38} />
          </Base>
        )
      }
      productId={product.id}
    />
  );
}
